import type { FC } from 'react'

import { Badge } from '@skytech/ui/Badge'
import RecycleIcon from 'mdi-react/RecycleIcon'
import { Link } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/components/i18n/Translation'
import { LocalDate } from '@/components/LocalDate'
import { useIsThreeStepIT } from '@/hooks/user'
import { APP_ROUTES } from '@/util/appRoutes'

import type { CoreGreenpoolOrder } from '../types'

import { getBadgeTypeFromStatus, isCoreOrder } from './helpers'

export const OrderListItem: FC<CoreGreenpoolOrder> = order => {
  const isThreeStepIT = useIsThreeStepIT()
  const { id, policyName, orderDate, dealerEmails, dealerPhoneNos, dealerName } = order
  const dealerPhoneNumber = dealerPhoneNos ? dealerPhoneNos.split(',')[0] : ''
  const dealerEmail = dealerEmails ? dealerEmails.split(',')[0] : ''
  const isContactInfoVisible =
    isCoreOrder(order) && Boolean(dealerName || dealerEmail || dealerPhoneNumber)
  const link = isCoreOrder(order)
    ? APP_ROUTES.ecomPolicyOrder.replace(':orderId', String(id))
    : APP_ROUTES.ecomPolicyGreenpoolOrder.replace(':orderId', String(id))

  return (
    <Link
      to={link}
      className="hover:shadow-basic-black focus:shadow-basic-black group flex flex-col justify-between rounded-lg bg-white p-4 text-black shadow-[0_2px_8px_0_rgba(99,99,99,0.2)] transition duration-300 ease-out"
    >
      <div
        className={twJoin(
          'flex flex-row-reverse',
          isCoreOrder(order) ? 'justify-end' : 'justify-between'
        )}
      >
        {!isCoreOrder(order) && (
          <div>
            <RecycleIcon
              className={twJoin(isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-badge')}
            />
          </div>
        )}
        <div>
          <p className="!m-0 !mb-1 text-base font-semibold text-black opacity-80 transition duration-300 ease-out group-hover:text-neutral-700 group-focus:text-neutral-700">
            {isCoreOrder(order) ? (
              <Translation id="Order #{orderId}" orderId={order.vendorSalesOrderNo || id} />
            ) : (
              <Translation id="Greenpool Order #{orderId}" orderId={id} />
            )}
          </p>
          {policyName && (
            <p className="!m-0 !mb-1 !mt-0.5 text-sm transition duration-300 ease-out">
              {policyName}
            </p>
          )}

          {isCoreOrder(order) && order.deliveryDate ? (
            <p className="!m-0 !mt-0.5 text-xs text-zinc-400 transition duration-300 ease-out">
              <Translation
                id="Leveringsdato: {deliveryDate}"
                deliveryDate={<LocalDate date={order.deliveryDate} />}
              />
            </p>
          ) : (
            <p className="!m-0 !mt-0.5 text-xs text-zinc-400 transition duration-300 ease-out">
              <Translation id="Ordredato: {orderDate}" orderDate={<LocalDate date={orderDate} />} />
            </p>
          )}

          {isContactInfoVisible && (
            <p className="!mx-0 !my-2 text-xs text-zinc-400 transition duration-300 ease-out">
              <Translation id="Contact:" />
              {dealerName ? ` ${dealerName}` : ''}
              {dealerEmail ? ',' : ''}
              {dealerEmail ? ` ${dealerEmail}` : ''}
              {dealerPhoneNumber ? ', ' : ''}
              {dealerPhoneNumber || ''}
            </p>
          )}
        </div>
      </div>
      {isCoreOrder(order) && (
        <Badge
          type={getBadgeTypeFromStatus(order.status)}
          size="small"
          className="mt-2 w-full lowercase"
        >
          <Translation id={`ECOM_ORDER_STATUS/${order.status}`} />
        </Badge>
      )}
    </Link>
  )
}
